import React from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TEXT_PLUGIN_COMPONENT_BASE} from 'notebook/mediocre/base-plugin/plugins';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import {
  DescriptionEditable,
  HeaderEditable,
  StepEditable,
  StepTimerEditable,
} from 'notebook/protocols/editor/StepsEditorEditables';
import {
  StepsEditor,
  StepsEditorPluginComponent,
  StepsEditorProps,
} from 'notebook/protocols/editor/StepsEditorTypes';
import {Step} from 'notebook/protocols/StepConstants';
import IntermediateBinder from 'ui-library/IntermediateBinder';
import APIUtil from 'util/APIUtil';

interface BoundProps {
  tabHandler: (index: number, ...args: Array<unknown>) => void;
  pasteHandler: (index: number, ...args: Array<unknown>) => void;
  updateHandler: (index: number, ...args: Array<unknown>) => void;
  formatHandler: (index: number, ...args: Array<unknown>) => void;
  moveHandler: (index: number, ...args: Array<unknown>) => void;
  multiSelectHandler: (index: number, ...args: Array<unknown>) => void;
  splitHandler: (index: number, ...args: Array<unknown>) => void;
  mergeUpHandler: (index: number, ...args: Array<unknown>) => void;
  updateTimeHandler: (time: number) => void;
  itemUpdateHandler: (step: Step) => void;
}

function buildStepComponent(
  Component: React.ComponentType,
  defaultText: {text: string; formats?: Format}
): StepsEditorPluginComponent {
  return _.defaults(
    {
      default(): {text: string; formats?: Format} {
        return defaultText;
      },
      required: [
        'tabHandler',
        'moveHandler',
        'textItemUpdateHandler',
        'itemUpdateHandler',
        'pasteHandler',
        'stepMergeUpHandler',
        'stepSplitHandler',
        'multiArrowSelectHandler',
        'itemUpdateHandler',
        'updateTimeHandler',
        'formatHandler',
      ],
      make(item: Step, index: number, editorProps: StepsEditorProps, itemProps: {}): JSX.Element {
        const cid = this.props.getCid(item);
        const {text, formats} = this.getText(item);
        return (
          <IntermediateBinder<BoundProps, number>
            key={cid}
            boundArg={index}
            tabHandler={this.tabHandler}
            pasteHandler={this.pasteHandler}
            updateHandler={this.textItemUpdateHandler}
            formatHandler={this.formatHandler}
            moveHandler={this.moveHandler}
            multiSelectHandler={this.multiArrowSelectHandler}
            splitHandler={this.stepSplitHandler}
            mergeUpHandler={this.stepMergeUpHandler}
            itemUpdateHandler={this.itemUpdateHandler}
            updateTimeHandler={this.updateTimeHandler}
          >
            {(boundProps) => (
              <Component
                // eslint-disable-next-line no-restricted-syntax
                {..._.extend(
                  {
                    text,
                    formats,
                    cid,
                    index,

                    storeState: editorProps.storeState,
                    restoreState: editorProps.restoreState,
                    inEntry: APIUtil.apiIdToType(editorProps.fileId) === ModelTypeConstants.ENTRY,
                    locked: editorProps.locked,
                    spellCheck: editorProps.spellCheck,

                    // for steps/desc
                    step: item,
                    setSelection: this.setSelection,
                  },
                  boundProps
                )}
              />
            )}
          </IntermediateBinder>
        );
      },
    },
    TEXT_PLUGIN_COMPONENT_BASE
  );
}
const defaultText = {text: '', formats: formatters.defaultFormats()};
export const HeaderComponent = buildStepComponent(HeaderEditable, defaultText);
// eslint-disable-next-line no-restricted-syntax
export const StepComponent = buildStepComponent(StepEditable, _.extend({completed: false}, defaultText));
export const DescriptionComponent = buildStepComponent(DescriptionEditable, defaultText);

export const TimerComponent: StepsEditorPluginComponent = {
  default(): number {
    return 60;
  },
  required: ['itemDeleteHandler', 'itemUpdateHandler', 'setSelection'],
  make(
    this: StepsEditor,
    item: Step,
    index: number,
    editorProps: StepsEditorProps,
    itemProps: {}
  ): JSX.Element {
    const cid = this.props.getCid(item);
    return (
      <StepTimerEditable
        key={cid}
        step={item}
        index={index}
        deleteHandler={this.itemDeleteHandler.bind(this, index)}
        updateTimerHandler={this.itemUpdateHandler.bind(this, index)}
        setSelection={this.setSelection}
        storeState={editorProps.storeState}
        restoreState={editorProps.restoreState}
        locked={editorProps.locked}
      />
    );
  },

  selectionAtStart(this: StepsEditor, item: Step): {selection: BenchlingSelection} {
    const start = 0;
    const end = 0;
    const cid = `time-${this.props.getCid(item)}`;
    return {selection: {cid, start, end}};
  },

  selectionAtEnd(this: StepsEditor, item: Step): {selection: BenchlingSelection} {
    const start = 6;
    const end = 6;
    const cid = `time-${this.props.getCid(item)}`;
    return {selection: {cid, start, end}};
  },

  selectionInit(this: StepsEditor, item: Step): {selection: BenchlingSelection} {
    const start = 6;
    const end = 6;
    const cid = `time-${this.props.getCid(item)}`;
    return {selection: {cid, start, end}};
  },
};
